import { PageController } from '@client/components/_core/layout/PageController';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { ComplaintWorkloadList } from '@client/components/complaints/complaints-workload/ComplaintWorkloadList';
import { OwnerTimelineLane } from '@client/components/complaints/complaints-workload/OwnerTimelineLane';
import { useTrackEngagementView } from '@client/hooks/business/engagement/useTrackEngagementView';
import { trpc } from '@client/trpc/client';
import { getFlag } from '@client/utils/feature-flags';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type Key, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/workload-manager/complaints')({
    staticData: {
        metadata: {
            title: 'Complaints',
        },
    },
    beforeLoad: async ({ params }): Promise<void> => {
        if (!(await getFlag('enable-complaints-workload', false))) {
            throw redirect({
                to: '/console/$orgId/workload-manager/tasks',
                params,
                replace: true,
            });
        }
    },
    component: WorkloadManagerComplaints,
});

function WorkloadManagerComplaints() {
    useTrackEngagementView('workload_tab_viewed', { tab: 'complaints' });
    const { data: ownersData, isLoading: ownersLoading } = trpc.complaint.getComplaintOwnerSignals.useQuery();

    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [selection, setSelection] = useState<Record<string, Key[]>>({});

    function toggleExpanded(ownerId: string) {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(ownerId)) next.delete(ownerId);
            else next.add(ownerId);
            return next;
        });
    }

    function setOwnerSelection(ownerId: string, keys: Key[]) {
        setSelection((prev) => ({ ...prev, [ownerId]: keys }));
    }

    // Dot click opens the lane (idempotent) and toggles the clicked complaint IDs
    // in the owner's selection. Never closes the lane.
    function handleDotClick(ownerId: string, ids: string[]) {
        setExpanded((prev) => {
            if (prev.has(ownerId)) return prev;
            const next = new Set(prev);
            next.add(ownerId);
            return next;
        });
        setSelection((prev) => {
            const current = prev[ownerId] ?? [];
            const currentSet = new Set(current.map(String));
            const allAlreadySelected = ids.every((id) => currentSet.has(id));
            const nextKeys: Key[] = allAlreadySelected
                ? current.filter((k) => !ids.includes(String(k)))
                : Array.from(new Set([...current.map(String), ...ids]));
            return { ...prev, [ownerId]: nextKeys };
        });
    }

    return (
        <PageController route={Route}>
            {ownersLoading ? (
                <Loading />
            ) : !ownersData || ownersData.length === 0 ? (
                <div className="p-6 text-muted-foreground">No complaint owners yet</div>
            ) : (
                <div className="flex flex-col divide-y divide-separator-secondary rounded-lg border border-separator bg-card">
                    {ownersData.map((row, i) => {
                        const isOpen = expanded.has(row.ownerId);
                        return (
                            <div key={row.ownerId}>
                                <OwnerTimelineLane
                                    owner={{ id: row.ownerId, name: row.ownerName, initials: row.ownerInitials }}
                                    signals={{
                                        pressureBucket: row.pressureBucket,
                                        openCount: row.openCount,
                                    }}
                                    ownComplaints={row.complaints}
                                    selected={isOpen}
                                    onSelect={() => toggleExpanded(row.ownerId)}
                                    onDotClick={(ids) => handleDotClick(row.ownerId, ids)}
                                    showAxis={i === 0}
                                />
                                {isOpen && (
                                    <div className="bg-muted/30 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                        <ComplaintWorkloadList
                                            filters={{ ownerId: row.ownerId }}
                                            rowSelection={{
                                                selectedRowKeys: selection[row.ownerId] ?? [],
                                                setSelectedRowKeys: (updater) => {
                                                    const current = selection[row.ownerId] ?? [];
                                                    const next =
                                                        typeof updater === 'function' ? updater(current) : updater;
                                                    setOwnerSelection(row.ownerId, next);
                                                },
                                            }}
                                            hideOwnerColumn
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </PageController>
    );
}
